import type { ImageCarouselBannerModel } from "src/features/HomepageCarouselBanners/models";
import type { ReactNode } from "react";

import { Image } from "lucide-react";
import { AzureImage } from "src/components/AssetUploaders/AzureImage";

export interface ImageCarouselBannerProps extends Partial<ImageCarouselBannerModel> {
	showPlaceholder?: boolean;

	showGradient?: boolean;
}

const ImageCarouselBanner = ({
	imageUuid,
	headingText,
	subheadingText,
	showPlaceholder = true,
	showGradient = false
}: ImageCarouselBannerProps) => {
	const renderPlaceholder = (children: ReactNode) => {
		if (!showPlaceholder) return null;

		return children;
	};

	const background =
		showGradient || headingText || subheadingText
			? "linear-gradient(0deg, #000 -38.1%, rgba(0, 0, 0, 0.00) 100%)"
			: undefined;

	return (
		<div
			className="relative bg-border w-full flex justify-center items-center"
			style={{ aspectRatio: 16 / 9 }}
		>
			{imageUuid ? (
				<AzureImage
					uuid={imageUuid}
					imageProfile="retail-app-homepage-carousel-mobile"
					style={{ objectFit: "cover", width: "100%", height: "100%" }}
					data-testid="banner-image"
				/>
			) : (
				renderPlaceholder(
					<Image
						size={40}
						opacity={0.2}
						color="#000000"
						data-testid="banner-image-placeholder"
					/>
				)
			)}

			<div
				className="absolute w-full bottom-0 px-6 pt-4 pb-6 flex flex-col justify-end"
				style={{ background }}
			>
				{headingText ? (
					<p className="text-white font-bold text-2xl leading-tight">
						{headingText}
					</p>
				) : (
					renderPlaceholder(
						<div
							className="w-1/2 h-[5px] bg-white rounded-[65px] mb-[5px]"
							data-testid="heading-text-placeholder"
						/>
					)
				)}

				{subheadingText ? (
					<p className="text-white text-base mt-1 opacity-80">
						{subheadingText}
					</p>
				) : (
					renderPlaceholder(
						<div
							className="w-1/4 h-[2px] bg-white rounded-[65px]"
							data-testid="subheading-text-placeholder"
						/>
					)
				)}
			</div>
		</div>
	);
};

export default ImageCarouselBanner;
